import { useEffect, useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';

import classNames from 'classnames';
import { z } from 'zod';

import { ConnectWalletGuard, useTokenBalanceByName } from '@shared/helpers/web3/wallet';

import { zodResolver } from '@hookform/resolvers/zod';
import { getAvailableTicketsForBalance } from '@store:web/components/entities/rewardPool';
import {
  DialogButton,
  DialogButtonsWrapper,
  DialogCancelButton,
} from '@ui/uikit/components/common/Dialog';
import { FormAmountInputWithPresets } from '@ui/uikit/components/common/FormAmountInputWithPresets';
import { FormControl } from '@ui/uikit/components/common/FormControl';
import { formatReward } from '@ui/uikit/utils';

import { useBuyTickets } from '../../../model/web3Raffle/api/useBuyTickets';
import { useTicketPrice } from '../../../model/web3Raffle/api/useTicketPrice';
import type {
  Web3RaffleSupportedToken} from '../../../model/web3Raffle/supportedTokens';
import {
  WEB3_RAFFLE_SUPPORTED_TOKENS
} from '../../../model/web3Raffle/supportedTokens';
import { SuccessPurchaseModal } from '../../modals/SuccessPurchaseModal/SuccessPurchaseModal';
import { SpecialOfferBanner } from './SpecialOfferBanner';
import { TicketPriceDetails } from './TicketPriceDetails';
import { TokenTabs } from './TokenTabs';

type Props = {
  onSubmit: () => void;
  className?: string;
};

const MIN_TICKET_AMOUNT = 1;

const renderHintMessage = (
  tokenName: Web3RaffleSupportedToken,
  balance?: number,
  errorMessage?: string,
) => {
  return (
    <div>
      <ConnectWalletGuard fallback="Connect Wallet">
        Wallet Balance: {formatReward(balance ?? 0)} {tokenName}
      </ConnectWalletGuard>
      {Boolean(errorMessage) && <div>{errorMessage}</div>}
    </div>
  );
};

export const GetTicketsForm = ({ onSubmit, className }: Props) => {
  const [defaultValues] = useState(() => {
    return { amount: '', tokenName: WEB3_RAFFLE_SUPPORTED_TOKENS[0] };
  });

  const FormSchema = useMemo(() => {
    return z.object({
      amount: z
        .string()
        .min(MIN_TICKET_AMOUNT, { message: 'Amount must be at least ' + MIN_TICKET_AMOUNT }),
      tokenName: z.enum(WEB3_RAFFLE_SUPPORTED_TOKENS),
    });
  }, []);

  const form = useForm<z.infer<typeof FormSchema>>({
    resolver: zodResolver(FormSchema),
    defaultValues,
  });
  const { handleSubmit, control, reset, watch, setError, clearErrors, formState } = form;

  const watchedAmount = watch('amount');
  const watchedTokenName = watch('tokenName');

  const { data: ticketPrice, isPending: isTicketPriceLoading } = useTicketPrice(watchedTokenName);
  const { data: tokenBalance, isPending: isTokenBalanceLoading } = useTokenBalanceByName({
    tokenName: watchedTokenName,
  });

  const { isDirty, errors, isSubmitting } = formState;
  const hasErrors = Object.keys(errors).length > 0;
  const isSubmitButtonDisabled =
    !isDirty ||
    hasErrors ||
    isSubmitting ||
    isTicketPriceLoading ||
    isTokenBalanceLoading ||
    !watchedAmount;

  const maxTicketsAmount = getAvailableTicketsForBalance({
    balance: tokenBalance?.value,
    price: ticketPrice?.value,
  });

  useEffect(() => {
    const validateAmount = () => {
      const amount = parseInt(watchedAmount, 10) || 0;
      const totalCost = amount * (ticketPrice?.value ?? 0);
      const balance = tokenBalance?.value ?? 0;

      if (watchedAmount && (totalCost > balance || balance === 0)) {
        setError('amount', {
          type: 'manual',
          message: 'Insufficient balance.',
        });
      } else {
        clearErrors('amount');
      }
    };

    validateAmount();
  }, [watchedAmount, ticketPrice?.value, tokenBalance?.value, setError, clearErrors]);

  const { buyTickets } = useBuyTickets();
  const [successModalOpen, setSuccessModalOpen] = useState(false);
  const [successTxHash, setsuccessTxHash] = useState('');

  const submit = handleSubmit(async (data) => {
    if (!ticketPrice?.value) {
      throw new Error('Ticket price is not available. Please try again later.');
    }

    const result = await buyTickets({
      amount: Number(data.amount),
      tokenName: data.tokenName,
      ticketPrice: ticketPrice?.value,
    });

    if (result) {
      setsuccessTxHash(result.linkToBlockExplorer ?? '');
      setSuccessModalOpen(true);
    }
  });

  const onCloseSuccessModal = () => {
    reset(defaultValues);
    setsuccessTxHash('');
    onSubmit();
  };

  return (
    <>
      {!!successTxHash && (
        <SuccessPurchaseModal
          open={successModalOpen}
          onOpenChange={setSuccessModalOpen}
          linkToBlockExplorer={successTxHash}
          ticketsAmount={Number(watchedAmount)}
          tokenName={watchedTokenName}
          onClose={onCloseSuccessModal}
        />
      )}
      <form
        onSubmit={submit}
        className={classNames('flex flex-col pt-6 max-md-msq:pt-4', className)}
      >
        <div className="flex flex-col gap-6 max-md-msq:gap-4">
          <FormControl label="Pay With">
            <Controller
              control={control}
              name="tokenName"
              render={({ field: { onChange, value } }) => {
                return <TokenTabs onChange={onChange} selectedToken={value} />;
              }}
            />
          </FormControl>
          <FormAmountInputWithPresets
            name="amount"
            label="Number of tickets"
            control={control}
            min={MIN_TICKET_AMOUNT}
            max={maxTicketsAmount}
            renderHintMessage={(errorMessage) =>
              renderHintMessage(watchedTokenName, tokenBalance?.value, errorMessage)
            }
          />
          <SpecialOfferBanner />
          <div className="mb-4 max-md-msq:mb-2">
            <TicketPriceDetails
              tokenName={watchedTokenName}
              ticketsAmount={Number(watchedAmount) ?? 0}
              price={ticketPrice?.value ?? 0}
              isLoading={isTicketPriceLoading}
            />
          </div>
        </div>
        <DialogButtonsWrapper>
          <ConnectWalletGuard>
            <DialogCancelButton />
            <DialogButton type="submit" isDisabled={isSubmitButtonDisabled}>
              Confirm
            </DialogButton>
          </ConnectWalletGuard>
        </DialogButtonsWrapper>
      </form>
    </>
  );
};
